import { AddCircleOutlined, DeleteOutlined, FreeBreakfastOutlined } from "@mui/icons-material";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useAllJobs } from "../../../hooks/useJobs";
import { useRoleColors } from "../../../hooks/useRoleColors";
import { useGetDepartmentNurseUnions } from "../../../pages/Profile/v2/hooks";
import { normalizeSelectedJobIds, selectedJobIdsForDisplay } from "../../../utils/jobSelection";
import type { BreakRulesConfig } from "../../../utils/models";
import { EmploymentType, NurseFullRole, NurseRole } from "../../../utils/models";
import { getEmploymentTypeDisplayName, nurseRoleDisplayName } from "../../../utils/utilFunctions";
import { SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { EmptyState } from "../../UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../UIToolkit/molecules/SettingsSection";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { ConfigMultiSelectCell } from "../../UIToolkit/Tables/ConfigTable/ConfigMultiSelectCell";
import {
	BORDERLESS_INPUT_SX,
	BORDERLESS_SELECT_SX,
	CONFIG_TABLE_BODY_CELL_SX,
	CONFIG_TABLE_CONTAINER_SX,
	CONFIG_TABLE_HEADER_CELL_SX,
	configTableBodyRowSx,
} from "../../UIToolkit/Tables/ConfigTable/configTableSx";
import { ConfigUnionCell } from "./ConfigUnionCell";
import { actionsColumn, BREAK_RULES_COLUMNS, columnWidthSx, visibleColumns } from "./wageRuleColumns";
import type { WageRulesTableProps } from "./wageRulesTableProps";

const BREAK_RULES_ACTIONS_COLUMN = actionsColumn("department.config.rates.breakRules.col.actions");

const BREAK_RULE_TYPE_OPTIONS = [
	{ value: "addition", labelKey: "department.config.rates.breakRules.type.addition" },
	{ value: "override", labelKey: "department.config.rates.breakRules.type.override" },
] as const;

/** Use shared display names from utilFunctions for consistency with Profile and rest of app. */
const EMPLOYMENT_TYPE_OPTIONS = (Object.values(EmploymentType) as EmploymentType[]).map((value) => ({
	value,
	label: getEmploymentTypeDisplayName(value),
	selectedLabel: getEmploymentTypeDisplayName(value),
}));

/**
 * The break rules table, edited on Team → Wage Settings (DAT-905). Moved from Settings →
 * Department Configuration, where it wrote through the whole-department config save.
 */
export const BreakRulesConfigTable: FC<WageRulesTableProps<BreakRulesConfig>> = ({
	rows: breakRulesConfigs,
	setRows,
	roles,
	jobIds,
	departmentId,
	disabled,
	highlightedRowIndices,
}) => {
	const roleColor = useRoleColors();
	const { t } = useTranslation();
	const { data: existingUnions } = useGetDepartmentNurseUnions();
	const { data: allJobs } = useAllJobs();
	const departmentJobs = useMemo(() => {
		const selected = new Set(jobIds ?? []);
		return (allJobs ?? []).filter((j) => selected.has(j.id));
	}, [allJobs, jobIds]);
	const showJobColumn = departmentJobs.length > 0;
	const columns = useMemo(() => [...visibleColumns(BREAK_RULES_COLUMNS, showJobColumn), BREAK_RULES_ACTIONS_COLUMN], [showJobColumn]);

	const addRow = useCallback(() => {
		setRows((prev) => [
			...prev,
			{
				id: uuidv4(),
				name: "",
				breakLimit: 0,
				// APP-450: seeded as the full sets, i.e. the form's "All". An empty selection is the
				// table's "nothing chosen" state, so a freshly added row used to disable Save the
				// moment it appeared, with the reason buried in the Save tooltip.
				roles: [...roles],
				jobIds: [],
				employmentTypes: Object.values(EmploymentType),
				type: "addition",
				breakTime: 0,
				// Seeded as "applies to everyone", matching how a pre-union row decodes from the API.
				union: [],
				isUnion: null,
				departmentId,
			} as BreakRulesConfig,
		]);
	}, [setRows, roles, departmentId]);

	const removeRow = useCallback(
		(index: number) => {
			setRows((prev) => prev.filter((_, i) => i !== index));
		},
		[setRows],
	);

	const updateRow = useCallback(
		(index: number, patch: Partial<BreakRulesConfig>) => {
			setRows((prev) => {
				const list = [...prev];
				list[index] = { ...list[index], ...patch };
				return list;
			});
		},
		[setRows],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
			<SettingsSection
				headerOnly
				icon={<FreeBreakfastOutlined />}
				title={t("department.config.rates.breakRules.section")}
				dataTestId="dept-config-rates-break-rules-header"
				action={
					<SystemButton
						buttonVariant="primary"
						size="small"
						disabled={disabled}
						leadingIcon={<AddCircleOutlined />}
						onClick={addRow}
						data-testid="dept-config-rates-break-rules-add-row"
					>
						{t("department.config.rates.addRow")}
					</SystemButton>
				}
			/>
			<TableContainer component={Paper} sx={CONFIG_TABLE_CONTAINER_SX}>
				<Table sx={{ minWidth: "max-content", width: "100%" }}>
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell key={column.id} sx={[CONFIG_TABLE_HEADER_CELL_SX, columnWidthSx(column)]}>
									{t(column.headerKey)}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{breakRulesConfigs.map((row, index) => (
							<TableRow
								key={row.id ?? index}
								sx={configTableBodyRowSx(highlightedRowIndices.has(index))}
								data-testid={`dept-config-rates-break-rules-row-${index}`}
								data-highlighted={highlightedRowIndices.has(index) ? "true" : undefined}
							>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<TextField
										size="small"
										disabled={disabled}
										fullWidth
										sx={BORDERLESS_INPUT_SX}
										value={row.name}
										onChange={(e) => updateRow(index, { name: e.target.value })}
										placeholder={t("department.config.rates.breakRules.col.name")}
										slotProps={{ htmlInput: { "data-testid": `dept-config-rates-break-rules-name-${index}` } }}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<TextField
										type="number"
										size="small"
										disabled={disabled}
										fullWidth
										sx={BORDERLESS_INPUT_SX}
										value={row.breakLimit ?? ""}
										onChange={(e) => updateRow(index, { breakLimit: e.target.value === "" ? 0 : Number(e.target.value) })}
										slotProps={{
											htmlInput: { min: 0, step: 0.5, "data-testid": `dept-config-rates-break-rules-break-limit-${index}` },
										}}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<TextField
										type="number"
										size="small"
										disabled={disabled}
										fullWidth
										sx={BORDERLESS_INPUT_SX}
										value={row.breakStart ?? ""}
										onChange={(e) => updateRow(index, { breakStart: e.target.value === "" ? undefined : Number(e.target.value) })}
										placeholder={t("department.config.rates.breakRules.breakStart.placeholder")}
										slotProps={{
											htmlInput: { min: 0, step: 0.5, "data-testid": `dept-config-rates-break-rules-break-start-${index}` },
										}}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<ConfigMultiSelectCell<NurseRole>
										disabled={disabled}
										ariaLabel={t("department.config.rates.breakRules.col.role")}
										dataTestId={`dept-config-rates-break-rules-roles-${index}`}
										items={roles.map((role) => ({
											value: role,
											label: NurseFullRole[role],
											selectedLabel: nurseRoleDisplayName[role] ?? role,
											color: roleColor(NurseFullRole[role]).dot,
										}))}
										selectedItems={(row.roles ?? []) as NurseRole[]}
										onChangeMultiple={(selectedValues) => {
											const nurseRoles = selectedValues.filter(
												(v): v is NurseRole => Object.keys(NurseRole).includes(String(v)) && roles.includes(v as NurseRole),
											) as NurseRole[];
											updateRow(index, { roles: nurseRoles.length === roles.length ? roles : nurseRoles });
										}}
										errorText={(row.roles == null || row.roles?.length === 0) && roles.length > 0 ? t("department.config.rates.chooseAllOrRoles") : undefined}
									/>
								</TableCell>
								{showJobColumn && (
									<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
										<ConfigMultiSelectCell<string>
											disabled={disabled}
											ariaLabel={t("department.config.rates.breakRules.col.job")}
											dataTestId={`dept-config-rates-break-rules-jobs-${index}`}
											items={departmentJobs.map((j) => ({ value: j.id, label: j.displayName ?? j.name, selectedLabel: j.displayName ?? j.name }))}
											selectedItems={selectedJobIdsForDisplay(row.jobIds, departmentJobs)}
											onChangeMultiple={(selectedValues) => {
												updateRow(index, { jobIds: normalizeSelectedJobIds(selectedValues as string[], departmentJobs) });
											}}
										/>
									</TableCell>
								)}
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<ConfigMultiSelectCell<EmploymentType>
										disabled={disabled}
										ariaLabel={t("department.config.rates.breakRules.col.employmentType")}
										dataTestId={`dept-config-rates-break-rules-employment-types-${index}`}
										items={EMPLOYMENT_TYPE_OPTIONS}
										selectedItems={(row.employmentTypes ?? []) as EmploymentType[]}
										onChangeMultiple={(selectedValues) => {
											const types = (selectedValues as string[]).filter((v) => Object.values(EmploymentType).includes(v as EmploymentType)) as EmploymentType[];
											updateRow(index, {
												employmentTypes: types.length === EMPLOYMENT_TYPE_OPTIONS.length ? (Object.values(EmploymentType) as EmploymentType[]) : types,
											});
										}}
										errorText={
											(row.employmentTypes == null || row.employmentTypes?.length === 0) && EMPLOYMENT_TYPE_OPTIONS.length > 0
												? t("department.config.rates.chooseAllOrEmploymentTypes")
												: undefined
										}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<ConfigUnionCell
										ariaLabel={t("department.config.rates.breakRules.col.union")}
										dataTestId={`dept-config-rates-break-rules-union-${index}`}
										isUnion={row.isUnion}
										union={row.union}
										existingUnions={existingUnions}
										disabled={disabled}
										onChange={(patch) => updateRow(index, patch)}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<SystemDropdown
										items={BREAK_RULE_TYPE_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))}
										selectedItem={row.type ?? "addition"}
										onChange={(v) => {
											if (v) updateRow(index, { type: v });
										}}
										selectSx={BORDERLESS_SELECT_SX}
										disabled={disabled}
										data-testid={`dept-config-rates-break-rules-type-${index}`}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<TextField
										type="number"
										size="small"
										disabled={disabled}
										fullWidth
										sx={BORDERLESS_INPUT_SX}
										value={row.breakTime ?? ""}
										onChange={(e) => updateRow(index, { breakTime: e.target.value === "" ? 0 : Number(e.target.value) })}
										slotProps={{
											htmlInput: { min: 1, step: 1, "data-testid": `dept-config-rates-break-rules-break-time-${index}` },
										}}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<SystemButton
										buttonVariant="borderless"
										size="small"
										disabled={disabled}
										leadingIcon={<DeleteOutlined fontSize="small" />}
										onClick={() => removeRow(index)}
										tooltip={t("department.config.rates.delete")}
										aria-label={t("department.config.rates.delete")}
										data-testid={`dept-config-rates-break-rules-delete-${index}`}
									/>
								</TableCell>
							</TableRow>
						))}
						{breakRulesConfigs.length === 0 && (
							<TableRow>
								<TableCell colSpan={columns.length} sx={{ borderBottom: "none", p: 0 }}>
									<EmptyState
										icon={<FreeBreakfastOutlined />}
										title={t("department.config.rates.breakRules.empty.title")}
										hint={t("department.config.rates.breakRules.empty.hint")}
										action={
											<SystemButton
												buttonVariant="secondary"
												size="small"
												disabled={disabled}
												leadingIcon={<AddCircleOutlined fontSize="small" />}
												onClick={addRow}
												data-testid="dept-config-rates-break-rules-empty-add"
											>
												{t("department.config.rates.addRow")}
											</SystemButton>
										}
										dataTestId="dept-config-rates-break-rules-empty"
									/>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};
